import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Link, LinkProps, useMatch, useResolvedPath } from "react-router-dom";
import { NavigationData } from "../config/data-navs";
import { RESUME_ENABLED, RESUME_PDF_PATH, RESUME_PDF_TITLE } from "../config/resume";
import ResumePreviewModal from "./resume-preview-modal";
import BrandLogo from "./brand-logo";
import ThemeToggle from "./theme-toggle";
import { devicesMax } from "../config/devices";
import useMediaQuery from "../hooks/UseMediaQuery";

type NavBarProps = {
  onOpenContact: () => void;
};

const CustomLink = ({ children, to, ...props }: LinkProps) => {
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: resolved.pathname, end: true });
  const active = !!match;

  return (
    <NavItemLink to={to} $active={active} {...props}>
      {children}
    </NavItemLink>
  );
};

const NavBar = ({ onOpenContact }: NavBarProps) => {
  const [open, setOpen] = useState(false);
  const [resumePreviewOpen, setResumePreviewOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const mediaTablet = useMediaQuery(devicesMax.tablet);

  const closeMenu = () => {
    if (!mediaTablet) return;
    setOpen(false);
    document.body.classList.remove("blur");
    document.getElementById("router-body")?.classList.remove("main-blur");
  };

  const openMenu = () => {
    setOpen(true);
    document.body.classList.add("blur");
    document.getElementById("router-body")?.classList.add("main-blur");
  };

  const toggleMenu = () => {
    if (open) closeMenu();
    else openMenu();
  };

  const openResumePreview = () => {
    closeMenu();
    setResumePreviewOpen(true);
  };

  const openContact = () => {
    closeMenu();
    onOpenContact();
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!mediaTablet && open) closeMenu();
  }, [mediaTablet, open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <NavBarWrapper id="site-header" $scrolled={scrolled}>
      <NavInner>
        <BrandLink to="/home" onClick={closeMenu} aria-label="Mark Jones Solano — Home">
          <BrandLogo size={40} />
          <BrandName>Mark Solano</BrandName>
        </BrandLink>

        <PrimaryNav id="primary-nav" $open={open} aria-label="Primary navigation">
          <NavList>
            <NavListItem>
              <CustomLink to="/home" onClick={closeMenu}>
                Home
              </CustomLink>
            </NavListItem>
            {NavigationData.map((nav) => (
              <NavListItem key={nav.Section}>
                <CustomLink to={nav.Section} onClick={closeMenu}>
                  {nav.Label}
                </CustomLink>
              </NavListItem>
            ))}
            {RESUME_ENABLED && (
              <NavListItem className="d-md-none">
                <ResumeNavButton type="button" onClick={openResumePreview}>
                  Resume
                </ResumeNavButton>
              </NavListItem>
            )}
          </NavList>
        </PrimaryNav>

        <HeaderActions>
          <ThemeToggle />
          {RESUME_ENABLED && (
            <HeaderCta type="button" onClick={openResumePreview}>
              View Resume
            </HeaderCta>
          )}
          <HeaderCta type="button" onClick={openContact}>
            Contact
          </HeaderCta>
          <NavToggle
            type="button"
            aria-expanded={open}
            aria-controls="primary-nav"
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            onClick={toggleMenu}
          >
            <NavToggleBar />
            <NavToggleBar />
            <NavToggleBar />
          </NavToggle>
        </HeaderActions>
      </NavInner>

      {mediaTablet && open && <NavBackdrop onClick={closeMenu} aria-hidden />}

      {RESUME_ENABLED && resumePreviewOpen && (
        <ResumePreviewModal
          src={RESUME_PDF_PATH}
          title={RESUME_PDF_TITLE}
          onClose={() => setResumePreviewOpen(false)}
        />
      )}
    </NavBarWrapper>
  );
};

export default NavBar;

const NavBarWrapper = styled.header<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: var(--header-height);
  background: var(--header-bg);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid
    ${({ $scrolled }) => ($scrolled ? "var(--border-color)" : "transparent")};
  box-shadow: ${({ $scrolled }) => ($scrolled ? "var(--shadow-sm)" : "none")};
  transition: border-color var(--transition), box-shadow var(--transition);
`;

const NavInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--header-height);
  gap: var(--space-lg);
  max-width: var(--container-max);
  margin: 0 auto;
  padding-inline: var(--space-lg);
`;

const BrandLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  text-decoration: none;
  flex-shrink: 0;
  z-index: 1001;

  &:focus-visible {
    outline: 2px solid var(--green);
    outline-offset: 4px;
    border-radius: var(--radius-sm);
  }
`;

const BrandName = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);

  @media ${devicesMax.tablet} {
    display: none;
  }
`;

const PrimaryNav = styled.nav<{ $open: boolean }>`
  display: none;

  @media (min-width: 768px) {
    display: block;
  }

  @media ${devicesMax.tablet} {
    display: ${({ $open }) => ($open ? "block" : "none")};
    position: fixed;
    top: var(--header-height);
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--bg-primary);
    padding: var(--space-xl);
    border-top: 1px solid var(--border-color);
    z-index: 999;
  }
`;

const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: var(--space-xl);

  @media ${devicesMax.tablet} {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-lg);
  }
`;

const NavListItem = styled.li`
  list-style: none;
`;

const navLinkStyles = css<{ $active?: boolean }>`
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${({ $active }) =>
    $active ? "var(--green)" : "var(--text-secondary)"};
  text-decoration: none;
  transition: color var(--transition);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    color: var(--green);
  }

  @media ${devicesMax.tablet} {
    font-size: 1.125rem;
  }
`;

const NavItemLink = styled(Link)<{ $active?: boolean }>`
  ${navLinkStyles}
`;

const ResumeNavButton = styled.button`
  ${navLinkStyles}
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-md);
  z-index: 1001;
`;

const HeaderCta = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: var(--radius-md);
  border: 1px solid var(--green);
  background: var(--green);
  color: var(--navy);
  cursor: pointer;
  transition: all var(--transition);
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    background: var(--white-pure);
    border-color: var(--white-pure);
    color: var(--navy);
  }

  html[data-theme="light"] & {
    background: var(--navy);
    border-color: var(--navy);
    color: var(--white-pure);

    &:hover {
      background: var(--accent-readable);
      border-color: var(--accent-readable);
      color: var(--white-pure);
    }
  }

  @media (min-width: 768px) {
    display: inline-flex;
  }
`;

const NavToggle = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 8px;

  @media (min-width: 768px) {
    display: none;
  }
`;

const NavToggleBar = styled.span`
  display: block;
  width: 100%;
  height: 2px;
  background: var(--text-primary);
  border-radius: 2px;
  transition: all var(--transition);
`;

const NavBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 998;
  background: var(--overlay-backdrop);
`;
